/** API client for /api/v1/trading/* endpoints. */

import type { PermissionsResponse, RbacRoleWithPermissions } from '../types/permissions';
import type { SignalSubtypeResponse } from '../types/strategy';
import type {
  AccountSnapshot,
  AnalysisJob,
  AnalysisJobListResponse,
  AnalysisJobSubmitResponse,
  CalibrationReport,
  EAMapping,
  EAMappingCreate,
  ForexNewsEvent,
  LearningInsight,
  MetricsHistoryEntry,
  MarketOutlook,
  NewsFilterResult,
  PerformanceMetrics,
  ProviderTestResult,
  QuickReviewResult,
  RbacAuditLogPage,
  RbacUserDetail,
  RbacUserSummary,
  SkillContent,
  TradingAnalysis,
  TradingConfig,
  TradingConfirmation,
  TradingTrade,
  UserListItem,
  UserTradingConfig,
  UserTradingConfigUpdate,
} from '../types/trading';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

class TradingApiService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    if (options.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      const err = new Error(error.detail || 'Request failed') as Error & { status: number; response: { status: number } };
      err.status = response.status;
      err.response = { status: response.status };
      throw err;
    }

    if (response.status === 204) return null as T;
    return response.json();
  }

  // ─── Generic HTTP helpers (used by admin pages) ───────────────

  async get<T = unknown>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(`/api/v1${endpoint}`);
    return { data };
  }

  async post<T = unknown>(endpoint: string, body?: unknown): Promise<{ data: T }> {
    const data = await this.request<T>(`/api/v1${endpoint}`, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async put<T = unknown>(endpoint: string, body?: unknown): Promise<{ data: T }> {
    const data = await this.request<T>(`/api/v1${endpoint}`, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async delete<T = unknown>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(`/api/v1${endpoint}`, { method: 'DELETE' });
    return { data };
  }

  // ─── Analyses ─────────────────────────────────────────────────

  async getAnalyses(
    params: { symbol?: string; limit?: number; offset?: number } = {}
  ): Promise<TradingAnalysis[]> {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<TradingAnalysis[]>(`/api/v1/trading/analyses${query}`);
  }

  async getAnalysis(id: string): Promise<TradingAnalysis> {
    return this.request<TradingAnalysis>(`/api/v1/trading/analysis/${id}`);
  }

  async deleteAnalysis(id: string): Promise<void> {
    await this.request<void>(`/api/v1/trading/analyses/${id}`, { method: 'DELETE' });
  }

  async deleteConfirmation(id: string): Promise<void> {
    await this.request<void>(`/api/v1/trading/confirmations/${id}`, { method: 'DELETE' });
  }

  async analyzeManual(file: File, symbol?: string, timeframe?: string, notes?: string): Promise<AnalysisJobSubmitResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (symbol) formData.append('symbol', symbol);
    if (timeframe) formData.append('timeframe', timeframe);
    if (notes) formData.append('notes', notes);

    return this.request<AnalysisJobSubmitResponse>('/api/v1/trading/analyze-manual', {
      method: 'POST',
      body: formData,
    });
  }

  // ─── Confirmations ──────────────────────────────────────────────

  async getConfirmations(
    params: { ea_name?: string; symbol?: string; limit?: number; offset?: number } = {}
  ): Promise<TradingConfirmation[]> {
    const qs = new URLSearchParams();
    if (params.ea_name) qs.set('ea_name', params.ea_name);
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<TradingConfirmation[]>(`/api/v1/trading/confirmations${query}`);
  }

  async getConfirmationScreenshots(confirmationId: string): Promise<{ confirmation_id: string; timeframes: string[] }> {
    return this.request(`/api/v1/trading/confirmations/${confirmationId}/screenshots`);
  }

  getConfirmationScreenshotUrl(confirmationId: string, timeframe: string): string {
    const token = this.getToken();
    const base = `${API_URL}/api/v1/trading/confirmations/${confirmationId}/screenshots/${timeframe}`;
    return token ? `${base}?token=${token}` : base;
  }

  // ─── Trades ───────────────────────────────────────────────────

  async getTrades(
    params: { symbol?: string; limit?: number; offset?: number } = {}
  ): Promise<TradingTrade[]> {
    const qs = new URLSearchParams();
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<TradingTrade[]>(`/api/v1/trading/trades${query}`);
  }

  async getTradeEntryScreenshots(ticket: number): Promise<{ ticket: number; timeframes: string[] }> {
    return this.request(`/api/v1/trading/trades/${ticket}/entry-screenshots`);
  }

  getTradeEntryScreenshotUrl(ticket: number, timeframe: string): string {
    const token = this.getToken();
    const base = `${API_URL}/api/v1/trading/trades/${ticket}/entry-screenshots/${timeframe}`;
    return token ? `${base}?token=${token}` : base;
  }

  // ─── Account ────────────────────────────────────────────────────

  async getAccountLatest(): Promise<AccountSnapshot> {
    return this.request<AccountSnapshot>('/api/v1/trading/admin/account/latest');
  }

  async getAccountHistory(days = 30): Promise<AccountSnapshot[]> {
    return this.request<AccountSnapshot[]>(`/api/v1/trading/admin/account/history?days=${days}`);
  }

  // ─── Admin: Insights ──────────────────────────────────────────

  async getInsights(status?: string): Promise<LearningInsight[]> {
    const query = status ? `?status=${status}` : '';
    return this.request<LearningInsight[]>(`/api/v1/trading/admin/insights${query}`);
  }

  async actionInsight(
    id: string,
    action: string,
    notes?: string,
    modifiedContent?: string,
    force?: boolean
  ): Promise<{ status: string; contradictions?: Array<{ existing_insight_id: string; existing_content: string; shared_topics: string[]; reason: string }>; message?: string }> {
    const query = force ? '?force=true' : '';
    return this.request('/api/v1/trading/admin/insights/' + id + '/action' + query, {
      method: 'POST',
      body: JSON.stringify({
        action,
        notes,
        modified_content: modifiedContent,
      }),
    });
  }

  // ─── Admin: Skills ────────────────────────────────────────────

  async getSkills(): Promise<string[]> {
    return this.request<string[]>('/api/v1/trading/admin/skills');
  }

  async getSkillBase(skillDir: string): Promise<SkillContent> {
    return this.request<SkillContent>(`/api/v1/trading/admin/skill/${skillDir}/base`);
  }

  async getSkillLearned(skillDir: string): Promise<SkillContent> {
    return this.request<SkillContent>(`/api/v1/trading/admin/skill/${skillDir}/learned`);
  }

  async updateSkillLearned(skillDir: string, content: string): Promise<{ status: string }> {
    return this.request(`/api/v1/trading/admin/skill/${skillDir}/learned`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
  }

  async resetSkillLearned(skillDir: string): Promise<{ status: string }> {
    return this.request(`/api/v1/trading/admin/skill/${skillDir}/reset`, {
      method: 'POST',
    });
  }

  // ─── Admin: Metrics ───────────────────────────────────────────

  async getMetrics(): Promise<PerformanceMetrics> {
    return this.request<PerformanceMetrics>('/api/v1/trading/admin/metrics/summary');
  }

  async getMetricsHistory(days = 30): Promise<MetricsHistoryEntry[]> {
    return this.request<MetricsHistoryEntry[]>(`/api/v1/trading/admin/metrics/history?days=${days}`);
  }

  // ─── Per-User Config ──────────────────────────────────────────

  async getUserConfig(): Promise<UserTradingConfig> {
    return this.request<UserTradingConfig>('/api/v1/trading/admin/user-config');
  }

  async updateUserConfig(updates: UserTradingConfigUpdate): Promise<UserTradingConfig> {
    return this.request<UserTradingConfig>('/api/v1/trading/admin/user-config', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // ─── Admin: Config ────────────────────────────────────────────

  async getConfig(): Promise<TradingConfig> {
    return this.request<TradingConfig>('/api/v1/trading/admin/config');
  }

  async updateConfig(updates: Partial<TradingConfig>): Promise<{ status: string }> {
    return this.request('/api/v1/trading/admin/config', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // ─── Admin: Reviews ────────────────────────────────────────────

  async triggerReview(period: 'daily' | 'weekly' | 'monthly'): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/trading/admin/review/${period}`, { method: 'POST' });
  }

  // ─── Trades: Single ────────────────────────────────────────────

  async getTrade(ticket: number): Promise<TradingTrade> {
    return this.request<TradingTrade>(`/api/v1/trading/trades/${ticket}`);
  }

  // ─── Signal Feedback ────────────────────────────────────────────

  async submitFeedback(
    analysisId: string,
    rating: string,
    notes?: string
  ): Promise<{ status: string }> {
    return this.request(`/api/v1/trading/analysis/${analysisId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ rating, notes }),
    });
  }

  // ─── Confirmation Feedback ──────────────────────────────────────

  async submitConfirmationFeedback(
    confirmationId: string,
    rating: string,
    notes?: string
  ): Promise<{ status: string }> {
    return this.request(`/api/v1/trading/confirmations/${confirmationId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ rating, notes }),
    });
  }

  // ─── Signal Validation ──────────────────────────────────────────

  async validateSignal(analysisId: string): Promise<TradingAnalysis> {
    return this.request<TradingAnalysis>(`/api/v1/trading/analysis/${analysisId}/validate`, {
      method: 'POST',
    });
  }

  // ─── Calibration ───────────────────────────────────────────────

  async getCalibrationReport(): Promise<CalibrationReport> {
    return this.request<CalibrationReport>('/api/v1/trading/admin/metrics/calibration');
  }

  // ─── Quick Review ──────────────────────────────────────────────

  async quickReviewTrade(ticket: number): Promise<QuickReviewResult> {
    return this.request<QuickReviewResult>(`/api/v1/trading/admin/review/trade/${ticket}`, {
      method: 'POST',
    });
  }

  // ─── Provider Test ──────────────────────────────────────────────

  async testProvider(prompt: string): Promise<ProviderTestResult> {
    return this.request<ProviderTestResult>('/api/v1/trading/admin/test-provider', {
      method: 'POST',
      body: JSON.stringify({ prompt }),
    });
  }

  // ─── Repair Trade Links ─────────────────────────────────────────

  async repairTradeLinks(): Promise<{ repaired: number; already_ok: number; unresolved: number }> {
    return this.request('/api/v1/trading/admin/repair-trade-links', { method: 'POST' });
  }

  // ─── Backfill Strategy Parameters ───────────────────────────────

  async backfillStrategyParameters(dryRun = false): Promise<{
    total: number;
    succeeded: number;
    failed: number;
    skipped: number;
    would_process: number;
    dry_run: boolean;
  }> {
    return this.request(`/api/v1/trading/admin/strategies/backfill-parameters?dry_run=${dryRun}`, { method: 'POST' });
  }

  // ─── Insight Effectiveness ─────────────────────────────────────

  async getInsightEffectiveness(id: string): Promise<Record<string, unknown>> {
    return this.request(`/api/v1/trading/admin/insights/${id}/effectiveness`);
  }

  // ─── Analysis Jobs (Async Queue) ──────────────────────────────────

  async getAnalysisJobs(
    params: { status?: string; symbol?: string; limit?: number } = {}
  ): Promise<AnalysisJobListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.symbol) qs.set('symbol', params.symbol);
    if (params.limit) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request<AnalysisJobListResponse>(`/api/v1/trading/jobs${query}`);
  }

  async getAnalysisJob(id: number): Promise<AnalysisJob> {
    return this.request<AnalysisJob>(`/api/v1/trading/jobs/${id}/detail`);
  }

  async retryJob(jobId: number): Promise<AnalysisJobSubmitResponse> {
    return this.request<AnalysisJobSubmitResponse>(`/api/v1/trading/jobs/${jobId}/retry`, {
      method: 'POST',
    });
  }

  // ─── EA Mappings ──────────────────────────────────────────────────

  async getEAMappings(): Promise<EAMapping[]> {
    return this.request<EAMapping[]>('/api/v1/trading/admin/ea-mappings');
  }

  async createEAMapping(data: EAMappingCreate): Promise<EAMapping> {
    return this.request<EAMapping>('/api/v1/trading/admin/ea-mappings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteEAMapping(eaName: string): Promise<{ status: string }> {
    return this.request(`/api/v1/trading/admin/ea-mappings/${encodeURIComponent(eaName)}`, {
      method: 'DELETE',
    });
  }

  async getUsersForMapping(): Promise<UserListItem[]> {
    return this.request<UserListItem[]>('/api/v1/trading/admin/users-list');
  }

  // ─── Forex News ──────────────────────────────────────────────────

  async getForexCalendar(): Promise<ForexNewsEvent[]> {
    return this.request<ForexNewsEvent[]>('/api/v1/trading/news/calendar');
  }

  async getUpcomingNews(symbol: string, hoursAhead = 4): Promise<ForexNewsEvent[]> {
    return this.request<ForexNewsEvent[]>(
      `/api/v1/trading/news/upcoming/${encodeURIComponent(symbol)}?hours_ahead=${hoursAhead}`
    );
  }

  async checkNewsFilter(symbol: string): Promise<NewsFilterResult> {
    return this.request<NewsFilterResult>(
      `/api/v1/trading/news/check/${encodeURIComponent(symbol)}`
    );
  }

  // ─── Market Outlook ────────────────────────────────────────────

  async getMarketOutlook(): Promise<MarketOutlook> {
    return this.request<MarketOutlook>('/api/v1/trading/market-outlook');
  }

  // ─── Admin: Expert Learnings CRUD ───────────────────────────────

  async getLearnings(
    params: {
      level?: string;
      domain?: string;
      source?: string;
      status?: string;
      user_id?: number;
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<{
    items: import('../types/expert').ExpertLearning[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const qs = new URLSearchParams();
    if (params.level) qs.set('level', params.level);
    if (params.domain) qs.set('domain', params.domain);
    if (params.source) qs.set('source', params.source);
    if (params.status) qs.set('status', params.status);
    if (params.user_id) qs.set('user_id', String(params.user_id));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request(`/api/v1/trading/admin/learnings${query}`);
  }

  async getLearning(id: number): Promise<import('../types/expert').ExpertLearning> {
    return this.request(`/api/v1/trading/admin/learnings/${id}`);
  }

  async createLearning(data: {
    content: string;
    level?: string;
    domain?: string;
    symbol?: string;
    strategy?: string;
    user_id?: number;
  }): Promise<import('../types/expert').ExpertLearning> {
    return this.request('/api/v1/trading/admin/learnings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateLearning(
    id: number,
    data: {
      content?: string;
      level?: string;
      domain?: string;
      symbol?: string;
      strategy?: string;
      status?: string;
    }
  ): Promise<import('../types/expert').ExpertLearning> {
    return this.request(`/api/v1/trading/admin/learnings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteLearning(id: number): Promise<{ status: string }> {
    return this.request(`/api/v1/trading/admin/learnings/${id}`, {
      method: 'DELETE',
    });
  }

  async getMyPermissions(): Promise<PermissionsResponse> {
    return this.request('/api/v1/auth/me/permissions');
  }

  // ─── RBAC Admin ────────────────────────────────────────────────

  async getRbacUsers(params: { q?: string; skip?: number; limit?: number } = {}): Promise<RbacUserSummary[]> {
    const qs = new URLSearchParams();
    if (params.q) qs.set('q', params.q);
    if (params.skip !== undefined) qs.set('skip', String(params.skip));
    if (params.limit !== undefined) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request(`/api/v1/admin/rbac/users${query}`);
  }

  async getRbacUserDetail(userId: number, includeExpired = false): Promise<RbacUserDetail> {
    return this.request(`/api/v1/admin/rbac/users/${userId}?include_expired=${includeExpired}`);
  }

  async grantRole(userId: number, roleName: string, expiresAt?: string): Promise<{ message: string }> {
    return this.request(`/api/v1/admin/rbac/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_name: roleName, expires_at: expiresAt ?? null }),
    });
  }

  async revokeRole(userId: number, roleName: string): Promise<{ message: string }> {
    return this.request(`/api/v1/admin/rbac/users/${userId}/roles/${roleName}`, {
      method: 'DELETE',
    });
  }

  async addPermissionOverride(
    userId: number,
    permissionKey: string,
    granted: boolean,
    reason: string,
    expiresAt?: string,
  ): Promise<{ message: string }> {
    return this.request(`/api/v1/admin/rbac/users/${userId}/overrides`, {
      method: 'POST',
      body: JSON.stringify({
        permission_key: permissionKey,
        granted,
        reason,
        expires_at: expiresAt ?? null,
      }),
    });
  }

  async removePermissionOverride(userId: number, permissionKey: string): Promise<{ message: string }> {
    return this.request(`/api/v1/admin/rbac/users/${userId}/overrides/${permissionKey}`, {
      method: 'DELETE',
    });
  }

  async getRbacAuditLog(params: {
    page?: number;
    page_size?: number;
    actor_email?: string;
    target_email?: string;
    action?: string;
    from_date?: string;
    to_date?: string;
  } = {}): Promise<RbacAuditLogPage> {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.page_size) qs.set('page_size', String(params.page_size));
    if (params.actor_email) qs.set('actor_email', params.actor_email);
    if (params.target_email) qs.set('target_email', params.target_email);
    if (params.action) qs.set('action', params.action);
    if (params.from_date) qs.set('from_date', params.from_date);
    if (params.to_date) qs.set('to_date', params.to_date);
    const query = qs.toString() ? `?${qs}` : '';
    return this.request(`/api/v1/admin/rbac/audit-log${query}`);
  }

  async getUserRbacAuditLog(
    userId: number,
    params: { page?: number; page_size?: number } = {},
  ): Promise<RbacAuditLogPage> {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.page_size) qs.set('page_size', String(params.page_size));
    const query = qs.toString() ? `?${qs}` : '';
    return this.request(`/api/v1/admin/rbac/users/${userId}/audit${query}`);
  }

  // ─── Role-Permission Matrix (v4-10.1) ─────────────────────────

  async getRbacRoles(): Promise<RbacRoleWithPermissions[]> {
    return this.request('/api/v1/admin/rbac/roles');
  }

  async addRolePermission(roleName: string, permissionKey: string): Promise<{ message: string }> {
    return this.request(`/api/v1/admin/rbac/roles/${roleName}/permissions`, {
      method: 'POST',
      body: JSON.stringify({ permission_key: permissionKey }),
    });
  }

  async removeRolePermission(roleName: string, permissionKey: string): Promise<{ message: string }> {
    return this.request(`/api/v1/admin/rbac/roles/${roleName}/permissions/${permissionKey}`, {
      method: 'DELETE',
    });
  }

  async processRefund(userId: number, amountCents: number): Promise<{ status: string; amount_cents: number; provider: string }> {
    return this.request(`/api/v1/admin/users/${userId}/refund`, {
      method: 'POST',
      body: JSON.stringify({ amount_cents: amountCents }),
    });
  }

  async extendTrial(userId: number, days: number): Promise<{ new_expires_at: string }> {
    return this.request(`/api/v1/admin/users/${userId}/extend-trial`, {
      method: 'POST',
      body: JSON.stringify({ days }),
    });
  }

  // ─── Signal Subtypes ──────────────────────────────────────────────

  async getSignalSubtypes(): Promise<SignalSubtypeResponse[]> {
    return this.request<SignalSubtypeResponse[]>('/api/v1/trading/signal-subtypes');
  }
}

export const tradingApi = new TradingApiService();
